import { useEffect, useRef } from 'react'
import { Cohort } from '@afterpaytouch/portal-api/types'
import { useLDClient } from '@afterpaytouch/feature'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import { Features } from '../../model'
import { newPersistentSession, useAppDispatch, useAuthentication, useConsumerSelector, useCohort, useInitialData, useIsTrustedCheckout } from '../../state'
import { isAfterpayInAppBrowser, isCashAppInAppBrowser } from '../../utils/device'
import { consumeInteractiveLoginSignal } from './loginSignal'
import { markRememberMeMinted } from './issuanceSignal'
import { hasOptedOutRememberMe } from './optOut'

/**
 * AP-cohort remember-me: mint a long-lived persistent session once per fresh interactive login.
 *
 * Mounted on the LoggedIn page, where every fresh login converges (recoverable / LCCC routing only
 * divert after startCheckout runs there). The trigger is the explicit one-shot login signal set by
 * the password / OTP / biometric / 2FA success handlers — see loginSignal.ts for why it is not
 * redux state. Mid-flow re-logins (session expiry / mid-recovery) skip this page and defer their
 * mint to the next fresh login.
 *
 * The mint lives post-login (rather than inline at the call sites) so the
 * CHECKOUT_LONG_LIVE_SESSION_ENABLED flag can be evaluated against a LaunchDarkly context keyed by
 * the consumer's uuid — enabling individual user targeting even on the first login on a device,
 * where the session-start LD context is still anonymous. Identification itself is owned by the
 * app-level LaunchDarklyContext (its userProps carry the consumer uuid post-login and it
 * re-identifies on key change, toggling hasFeatureFlagsReady) — this hook never identifies; it
 * waits until the client context key matches the logged-in consumer and LD reports ready.
 *
 * Gates: not a persistent (silently recognised) or trusted-checkout session (those must not
 * re-mint — each mint emails the consumer), Afterpay cohort only, not an iFrame checkout (the
 * device-trust cookie can't be set in a third-party iframe), not a managed user-agent
 * (Afterpay / Cash app webviews are out of scope), and not a device the consumer opted out of
 * remembering (CASHDAP-3292 issuance-toast "Not on this device").
 */
export const useMintPersistentSessionOnLogin = (): void => {
  const dispatch = useAppDispatch()
  const { persistent } = useAuthentication()
  const isTrustedCheckout = useIsTrustedCheckout()
  const cohort = useCohort()
  const { isIframe } = useInitialData()
  const { uuid, hasFeatureFlagsReady } = useConsumerSelector()
  const ldClient = useLDClient()
  // The one-shot signal is captured into a ref so transient waits below (LD identify in flight)
  // can re-run the effect without losing the pending mint.
  const loginPendingMint = useRef(false)

  useEffect(() => {
    if (consumeInteractiveLoginSignal('remember-me-mint')) {
      loginPendingMint.current = true
    }
    if (!loginPendingMint.current) {
      return
    }
    if (persistent || isTrustedCheckout) {
      loginPendingMint.current = false
      return
    }
    if (cohort !== Cohort.APOnly || isIframe || isAfterpayInAppBrowser() || isCashAppInAppBrowser()) {
      loginPendingMint.current = false
      return
    }
    // CASHDAP-3292: the consumer opted out on this device ("Not on this device" on the issuance
    // toast) — never mint here again, for any account. Checked per-login (not hoisted) so an
    // opt-out set earlier in this same session is honoured.
    if (hasOptedOutRememberMe()) {
      loginPendingMint.current = false
      return
    }
    if (isUndefinedOrEmpty(uuid) || ldClient === undefined) {
      return
    }
    // Until the client's context key matches this consumer AND LD reports ready, variation()
    // could return a stale (localStorage-bootstrapped or pre-login) value — minting on a stale
    // `true` would bypass the kill switch and miss individual targeting. Wait without clearing
    // the pending mint; the hasFeatureFlagsReady flip re-runs this effect once the app-level
    // identify completes.
    const context = ldClient.getContext?.() as { key?: string } | undefined
    if (context?.key !== uuid || hasFeatureFlagsReady !== true) {
      return
    }
    loginPendingMint.current = false

    void (async () => {
      try {
        if (ldClient.variation(Features.CHECKOUT_LONG_LIVE_SESSION_ENABLED, false) === true) {
          const result = await dispatch(newPersistentSession())
          // CASHDAP-3292: tell the summary issuance toast a mint actually happened this visit. Only
          // a fulfilled true counts — a rejected or declined mint must not show "we'll remember
          // this device".
          if (newPersistentSession.fulfilled.match(result) && result.payload === true) {
            markRememberMeMinted()
          }
        }
      } catch {
        // Minting is best-effort: never block the post-login flow on remember-me.
      }
    })()
  }, [persistent, isTrustedCheckout, cohort, isIframe, uuid, hasFeatureFlagsReady, ldClient, dispatch])
}
